import { SegmentFilterCategorySpecificField, SegmentFilterState } from 'domains/segments/types';
import { FilterGroupNode, FilterTreeLeaf, FilterTreeNode } from './types';

let nextId = 0;

/**
 * Generates a unique string ID for tree nodes.
 */
export const generateNodeId = (): string => {
    nextId += 1;
    return `node-${nextId}`;
};

/**
 * Resets the node ID counter. Useful when rebuilding the tree (e.g., audience copy).
 */
export const resetNodeIdCounter = (startFrom: number = 0): void => {
    nextId = startFrom;
};

/**
 * Creates a new filter leaf node wrapping a SegmentFilterState.
 */
export const createFilterLeafNode = (
    filter: SegmentFilterState<SegmentFilterCategorySpecificField>
): FilterTreeLeaf => ({
    type: 'filter',
    id: generateNodeId(),
    filter,
});

/**
 * Creates a new group node with optional initial children.
 * If no children are provided, the group starts with an empty array.
 */
export const createGroupNode = (matchType: 'AND' | 'OR' = 'AND', children: FilterTreeNode[] = []): FilterTreeNode => ({
    type: 'group',
    id: generateNodeId(),
    group: {
        id: generateNodeId(),
        matchType,
        children,
    },
});

/**
 * Adds a node to the end of the children array of the group identified by path.
 * Path [] targets the root group.
 */
export const addNodeAtPath = (root: FilterGroupNode, path: number[], node: FilterTreeNode): FilterGroupNode => {
    if (path.length === 0) {
        return { ...root, children: [...root.children, node] };
    }

    const [head, ...rest] = path;
    return {
        ...root,
        children: root.children.map((child, i) => {
            if (i !== head || child.type !== 'group') return child;
            return { ...child, group: addNodeAtPath(child.group, rest, node) };
        }),
    };
};

/**
 * Removes the node at the given path.
 * The last element of the path is the child index to remove within its parent group.
 */
export const removeNodeAtPath = (root: FilterGroupNode, path: number[]): FilterGroupNode => {
    if (path.length === 0) return root;

    if (path.length === 1) {
        return {
            ...root,
            children: root.children.filter((_, i) => i !== path[0]),
        };
    }

    const [head, ...rest] = path;
    return {
        ...root,
        children: root.children.map((child, i) => {
            if (i !== head || child.type !== 'group') return child;
            return { ...child, group: removeNodeAtPath(child.group, rest) };
        }),
    };
};

/**
 * Updates a filter leaf node at the given path with new filter data.
 */
export const updateFilterAtPath = (
    root: FilterGroupNode,
    path: number[],
    filterData: SegmentFilterState<SegmentFilterCategorySpecificField>
): FilterGroupNode => {
    if (path.length === 0) return root;

    if (path.length === 1) {
        return {
            ...root,
            children: root.children.map((child, i) => {
                if (i !== path[0] || child.type !== 'filter') return child;
                return { ...child, filter: filterData };
            }),
        };
    }

    const [head, ...rest] = path;
    return {
        ...root,
        children: root.children.map((child, i) => {
            if (i !== head || child.type !== 'group') return child;
            return { ...child, group: updateFilterAtPath(child.group, rest, filterData) };
        }),
    };
};

/**
 * Updates the matchType of the group at the given path.
 * Path [] targets the root group.
 */
export const updateMatchTypeAtPath = (
    root: FilterGroupNode,
    path: number[],
    matchType: 'AND' | 'OR'
): FilterGroupNode => {
    if (path.length === 0) {
        return { ...root, matchType };
    }

    const [head, ...rest] = path;
    return {
        ...root,
        children: root.children.map((child, i) => {
            if (i !== head || child.type !== 'group') return child;
            return { ...child, group: updateMatchTypeAtPath(child.group, rest, matchType) };
        }),
    };
};

/**
 * Collects all leaf filter nodes from the tree into a flat array (depth-first).
 */
export const flattenFilters = (root: FilterGroupNode): SegmentFilterState<SegmentFilterCategorySpecificField>[] => {
    const result: SegmentFilterState<SegmentFilterCategorySpecificField>[] = [];

    for (const child of root.children) {
        if (child.type === 'filter') {
            result.push(child.filter);
        } else {
            result.push(...flattenFilters(child.group));
        }
    }

    return result;
};

/**
 * Filters the tree, keeping only leaf nodes that satisfy the predicate.
 * Groups are preserved structurally (even if they become empty).
 */
export const filterTree = (
    root: FilterGroupNode,
    predicate: (filter: SegmentFilterState<SegmentFilterCategorySpecificField>) => boolean
): FilterGroupNode => ({
    ...root,
    children: root.children
        .map((child): FilterTreeNode | null => {
            if (child.type === 'filter') {
                return predicate(child.filter) ? child : null;
            }
            return { ...child, group: filterTree(child.group, predicate) };
        })
        .filter((child): child is FilterTreeNode => child !== null),
});

/**
 * Counts total number of leaf filter nodes in the tree.
 */
export const countFilters = (root: FilterGroupNode): number => {
    let count = 0;

    for (const child of root.children) {
        if (child.type === 'filter') {
            count += 1;
        } else {
            count += countFilters(child.group);
        }
    }

    return count;
};

/**
 * Recursively removes any group nodes whose children array is empty.
 * This cascades upward — if removing a child group makes its parent group
 * empty, that parent is pruned too. The root group itself is never pruned.
 */
export const pruneEmptyGroups = (root: FilterGroupNode): FilterGroupNode => ({
    ...root,
    children: root.children
        .map((child): FilterTreeNode | null => {
            if (child.type === 'filter') return child;
            const prunedGroup = pruneEmptyGroups(child.group);
            // If the group has no children left after pruning, remove it
            return prunedGroup.children.length === 0 ? null : { ...child, group: prunedGroup };
        })
        .filter((child): child is FilterTreeNode => child !== null),
});
